// src/private/src/pages/app/clients/components/ExportButton.jsx
import React, { memo, useState, useCallback } from 'react';
import { Button, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const SKIP_KEYS = new Set(['uuid', 'ingestion_health', 'spendWithFees', 'displayName']);

const COLUMN_LABELS = {
  date: 'Date',
  client__name: 'Client',
  client_id: 'Client ID',
  total_spend_sum: 'Spend',
  total_leads_sum: 'Leads',
  cost_per_lead_avg: 'CPL',
  self_books_sum: 'Self Books',
  accepted_sum: 'Accepted',
  success_sum: 'Success',
  out_of_areas_count_sum: 'Out of Area',
  booked_sum: 'Booked',
  scheduled_sum: 'Scheduled',
  issues_sum: 'Issues',
  demos_sum: 'Demos',
  gross_sales_sum: 'Gross Sold',
  gross_amount_sum: 'Gross Amount',
  net_sales_sum: 'Net Sold',
  net_amount_sum: 'Net Amount',
  vertical: 'Vertical',
  channel__name: 'Channel',
  campaign: 'Campaign',
  campaign_id: 'Campaign ID',
  territory_name: 'Territory',
  client_territory__name: 'Territory',
  state: 'State',
  state_name: 'State',
  dma__name: 'DMA',
  dma_name: 'DMA',
  city: 'City',
  city_name: 'City',
  county: 'County',
  county_name: 'County',
  month: 'Month',
};

const friendlyHeader = (key) => COLUMN_LABELS[key] || key.replace(/_/g, ' ');

const escCsv = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val);
  return str.includes(',') || str.includes('"') || str.includes('\n')
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const buildSection = (title, rows) => {
  if (!Array.isArray(rows) || !rows.length) return '';
  const headers = Object.keys(rows[0]).filter((k) => !SKIP_KEYS.has(k));
  const lines = [
    `--- ${title} ---`,
    headers.map(friendlyHeader).join(','),
    ...rows.map((row) => headers.map((h) => escCsv(row[h])).join(',')),
    '',
  ];
  return lines.join('\n');
};

/**
 * Export button with dropdown for CSV and PDF export.
 *
 * CSV export now includes ALL dashboard tables as labeled sections:
 * Summary, Verticals, Channels, Campaigns, Territories, States, DMAs,
 * Cities, Counties, MoM, DoD.
 *
 * @param {object} tables - Object with keys for each table's data array:
 *   { summary, verticals, channels, campaigns, territories, states, dmas,
 *     cities, counties, momData, dodData }
 */
function ExportButton({ tables = {}, clientName = 'Client', dateRange = '' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleExportCsv = useCallback(() => {
    handleClose();
    setExporting(true);
    try {
      const sections = [
        buildSection('Summary by Date', tables.summary),
        buildSection('Vertical Performance', tables.verticals),
        buildSection('Channel Performance', tables.channels),
        buildSection('Campaign Performance', tables.campaigns),
        buildSection('Territory Performance', tables.territories),
        buildSection('State Performance', tables.states),
        buildSection('DMA Performance', tables.dmas),
        buildSection('City Performance', tables.cities),
        buildSection('County Performance', tables.counties),
        buildSection('ZIP Performance', tables.zips),
        buildSection('Month-over-Month', tables.momData),
        buildSection('Day-over-Day', tables.dodData),
      ].filter(Boolean);

      if (!sections.length) {
        setExporting(false);
        return;
      }

      const header = `${clientName} Dashboard Export\nDate Range: ${dateRange}\nExported: ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })}\n\n`;
      const csv = header + sections.join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${clientName.replace(/[^a-z0-9]/gi, '_')}_dashboard_${dateRange.replace(/\s/g, '_')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [tables, clientName, dateRange, handleClose]);

  const handleExportPdf = useCallback(() => {
    handleClose();
    window.print();
  }, [handleClose]);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadIcon />}
        onClick={handleClick}
        disabled={exporting}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
      >
        Export
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleExportCsv}>
          <ListItemIcon><TableChartIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download CSV (all tables)</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportPdf}>
          <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Print / Save PDF</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default memo(ExportButton);
